import type {SbRichTextDoc as ISbRichtext, SbRichTextNode} from "@storyblok/js";
import sanitizeHtml from "sanitize-html";

export type Options = {
	// If not set, the function result may contain duplicate spaces between some ex rich text elements such as bolded text and regular text.
	preventExtraSpaces?: boolean;
};

const multiSpaceRegex = /\s+/g;

export const richTextObjectToString = (node: SbRichTextNode): string => {
	if (node.type === "text" && typeof node.text === "string") {
		return node.text;
	}
	// Non-text nodes should have inner `content` as an array
	if (Array.isArray(node.content)) {
		return node.content.map((x) => richTextObjectToString(x)).join(" ");
	}
	return "";
};

/**
 * Turn SB rich text into just its textual contents, separated by spaces. NOTE that this means no line breaks will be generated between what used to be paragraph nodes etc! Preferably use this for content that's not expected to have line breaks.
 *
 * To preserve newlines, you can try to use a more sophisticated method such as rendering the object into HTML with the `renderRichText` SB function, then calling something like `sanitizeHtml(html, { allowedTags: [] }).replaceAll('\n', '')` (package sanitize-html)
 */
export const richTextToString = (
	content: ISbRichtext | string | null | undefined,
	options?: Options,
): string => {
	// We allow empty values to be passed in so that the result is an empty string
	if (!content) {
		return "";
	}

	// Old stories may have their rich text field content as SB's legacy HTML format, handle that here
	if (typeof content === "string") {
		return sanitizeHtml(content, {allowedTags: []});
	}

	const res = richTextObjectToString(content);

	if (options?.preventExtraSpaces) {
		return res.replaceAll(multiSpaceRegex, " ");
	}

	return res;
};
